import type { VirtualBackgroundFilter } from '../../../webrtc/media';

export enum VirtualBackgroundGroups {
  NoEffectsBlur = 'NoEffectsBlur',
  Branded = 'Branded',
  Simple = 'Simple',
  Office = 'Office',
  Medical = 'Medical',
  Outdoor = 'Outdoor',
  Fun = 'Fun',
}

export type VirtualBackground = {
  /**
   * A unique ID that is compatible with backgroundEffect in OG.
   */
  index: number;
  group: VirtualBackgroundGroups;
  name: string;
  filter: VirtualBackgroundFilter | null;
  metadata: {
    title: string;
    label?: string;
    thumbnail?: string;
  };
  default?: boolean;
};

export const defaultVirtualBackground: VirtualBackground = {
  index: 0,
  group: VirtualBackgroundGroups.NoEffectsBlur,
  name: 'None',
  filter: null,
  metadata: {
    title: 'virtualBackground.options.none',
    label: 'virtualBackground.options.none',
  },
  default: true,
};

export const backgrounds: VirtualBackground[] = [
  defaultVirtualBackground,
  {
    index: 1,
    group: VirtualBackgroundGroups.NoEffectsBlur,
    name: 'Slight Blur',
    filter: { type: 'backgroundBlur', blurStrength: 'low' },
    metadata: {
      title: 'virtualBackground.options.slightBlur',
      label: 'virtualBackground.options.slightBlur',
    },
    default: true,
  },
  {
    index: 2,
    group: VirtualBackgroundGroups.NoEffectsBlur,
    name: 'Blur',
    filter: { type: 'backgroundBlur', blurStrength: 'high' },
    metadata: {
      title: 'virtualBackground.options.blur',
      label: 'virtualBackground.options.blur',
    },
    default: true,
  },
  {
    index: 3,
    group: VirtualBackgroundGroups.Office,
    name: 'Classic office with brown couch',
    filter: {
      type: 'backgroundReplacement',
      backgroundImgUrl: '/images/virtualBackgrounds/officehome1.jpg',
    },
    metadata: {
      title: 'virtualBackground.options.officehome1',
      thumbnail: '/images/virtualBackgrounds/officehome1.thumbnail.webp',
    },
  },
  {
    index: 4,
    group: VirtualBackgroundGroups.Office,
    name: 'Living room with large windows',
    filter: {
      type: 'backgroundReplacement',
      backgroundImgUrl: '/images/virtualBackgrounds/officehome2.jpg',
    },
    metadata: {
      title: 'virtualBackground.options.officehome2',
      thumbnail: '/images/virtualBackgrounds/officehome2.thumbnail.webp',
    },
  },
  {
    index: 5,
    group: VirtualBackgroundGroups.Office,
    name: 'Office with couch and plants',
    filter: {
      type: 'backgroundReplacement',
      backgroundImgUrl: '/images/virtualBackgrounds/officehome3.jpg',
    },
    metadata: {
      title: 'virtualBackground.options.officehome3',
      thumbnail: '/images/virtualBackgrounds/officehome3.thumbnail.webp',
    },
  },
  {
    index: 18,
    group: VirtualBackgroundGroups.Office,
    name: 'Rustic office with desk',
    filter: {
      type: 'backgroundReplacement',
      backgroundImgUrl: '/images/virtualBackgrounds/officehome4.jpg',
    },
    metadata: {
      title: 'virtualBackground.options.officehome4',
      thumbnail: '/images/virtualBackgrounds/officehome4.thumbnail.webp',
    },
  },
  {
    index: 19,
    group: VirtualBackgroundGroups.Office,
    name: 'Brown bookshelves',
    filter: {
      type: 'backgroundReplacement',
      backgroundImgUrl: '/images/virtualBackgrounds/officehome5.jpg',
    },
    metadata: {
      title: 'virtualBackground.options.officehome5',
      thumbnail: '/images/virtualBackgrounds/officehome5.thumbnail.webp',
    },
  },
  {
    index: 20,
    group: VirtualBackgroundGroups.Office,
    name: 'White bookshelves',
    filter: {
      type: 'backgroundReplacement',
      backgroundImgUrl: '/images/virtualBackgrounds/officehome6.jpg',
    },
    metadata: {
      title: 'virtualBackground.options.officehome6',
      thumbnail: '/images/virtualBackgrounds/officehome6.thumbnail.webp',
    },
  },
  {
    index: 6,
    group: VirtualBackgroundGroups.Medical,
    name: 'Wide open office with large window',
    filter: {
      type: 'backgroundReplacement',
      backgroundImgUrl: '/images/virtualBackgrounds/medical1.jpg',
    },
    metadata: {
      title: 'virtualBackground.options.medical1',
      thumbnail: '/images/virtualBackgrounds/medical1.thumbnail.webp',
    },
  },
  {
    index: 21,
    group: VirtualBackgroundGroups.Medical,
    name: 'Medical exam room in rural setting',
    filter: {
      type: 'backgroundReplacement',
      backgroundImgUrl: '/images/virtualBackgrounds/medical2.jpg',
    },
    metadata: {
      title: 'virtualBackground.options.medical2',
      thumbnail: '/images/virtualBackgrounds/medical2.thumbnail.webp',
    },
  },
  {
    index: 22,
    group: VirtualBackgroundGroups.Medical,
    name: 'Medical exam room in urban setting',
    filter: {
      type: 'backgroundReplacement',
      backgroundImgUrl: '/images/virtualBackgrounds/medical3.jpg',
    },
    metadata: {
      title: 'virtualBackground.options.medical3',
      thumbnail: '/images/virtualBackgrounds/medical3.thumbnail.webp',
    },
  },
  {
    index: 7,
    group: VirtualBackgroundGroups.Outdoor,
    name: 'Beachside patio with sunset',
    filter: {
      type: 'backgroundReplacement',
      backgroundImgUrl: '/images/virtualBackgrounds/outdoor1.jpg',
    },
    metadata: {
      title: 'virtualBackground.options.outdoor1',
      thumbnail: '/images/virtualBackgrounds/outdoor1.thumbnail.webp',
    },
  },
  {
    index: 8,
    group: VirtualBackgroundGroups.Outdoor,
    name: 'River valley with large tree',
    filter: {
      type: 'backgroundReplacement',
      backgroundImgUrl: '/images/virtualBackgrounds/outdoor2.jpg',
    },
    metadata: {
      title: 'virtualBackground.options.outdoor2',
      thumbnail: '/images/virtualBackgrounds/outdoor2.thumbnail.webp',
    },
  },
  {
    index: 23,
    group: VirtualBackgroundGroups.Outdoor,
    name: 'River with green rolling hills',
    filter: {
      type: 'backgroundReplacement',
      backgroundImgUrl: '/images/virtualBackgrounds/outdoor3.jpg',
    },
    metadata: {
      title: 'virtualBackground.options.outdoor3',
      thumbnail: '/images/virtualBackgrounds/outdoor3.thumbnail.webp',
    },
  },
  {
    index: 24,
    group: VirtualBackgroundGroups.Outdoor,
    name: 'Mountain lake',
    filter: {
      type: 'backgroundReplacement',
      backgroundImgUrl: '/images/virtualBackgrounds/outdoor4.jpg',
    },
    metadata: {
      title: 'virtualBackground.options.outdoor4',
      thumbnail: '/images/virtualBackgrounds/outdoor4.thumbnail.webp',
    },
  },
  {
    index: 25,
    group: VirtualBackgroundGroups.Outdoor,
    name: 'Mountainous setting',
    filter: {
      type: 'backgroundReplacement',
      backgroundImgUrl: '/images/virtualBackgrounds/outdoor5.jpg',
    },
    metadata: {
      title: 'virtualBackground.options.outdoor5',
      thumbnail: '/images/virtualBackgrounds/outdoor5.thumbnail.webp',
    },
  },
  {
    index: 9,
    group: VirtualBackgroundGroups.Simple,
    name: 'Creme colored design',
    filter: {
      type: 'backgroundReplacement',
      backgroundImgUrl: '/images/virtualBackgrounds/simple1.jpg',
    },
    metadata: {
      title: 'virtualBackground.options.simple1',
      thumbnail: '/images/virtualBackgrounds/simple1.thumbnail.webp',
    },
  },
  {
    index: 10,
    group: VirtualBackgroundGroups.Simple,
    name: 'Peach colored design',
    filter: {
      type: 'backgroundReplacement',
      backgroundImgUrl: '/images/virtualBackgrounds/simple2.jpg',
    },
    metadata: {
      title: 'virtualBackground.options.simple2',
      thumbnail: '/images/virtualBackgrounds/simple2.thumbnail.webp',
    },
  },
  {
    index: 11,
    group: VirtualBackgroundGroups.Simple,
    name: 'Beige colored design',
    filter: {
      type: 'backgroundReplacement',
      backgroundImgUrl: '/images/virtualBackgrounds/simple3.jpg',
    },
    metadata: {
      title: 'virtualBackground.options.simple3',
      thumbnail: '/images/virtualBackgrounds/simple3.thumbnail.webp',
    },
  },
  {
    index: 15,
    group: VirtualBackgroundGroups.Simple,
    name: 'Red and white wavy design',
    filter: {
      type: 'backgroundReplacement',
      backgroundImgUrl: '/images/virtualBackgrounds/simple4.jpg',
    },
    metadata: {
      title: 'virtualBackground.options.simple4',
      thumbnail: '/images/virtualBackgrounds/simple4.thumbnail.webp',
    },
  },
  {
    index: 16,
    group: VirtualBackgroundGroups.Simple,
    name: 'Gray wall with flowers',
    filter: {
      type: 'backgroundReplacement',
      backgroundImgUrl: '/images/virtualBackgrounds/simple5.jpg',
    },
    metadata: {
      title: 'virtualBackground.options.simple5',
      thumbnail: '/images/virtualBackgrounds/simple5.thumbnail.webp',
    },
  },
  {
    index: 17,
    group: VirtualBackgroundGroups.Simple,
    name: 'Blue wall with chair',
    filter: {
      type: 'backgroundReplacement',
      backgroundImgUrl: '/images/virtualBackgrounds/simple6.jpg',
    },
    metadata: {
      title: 'virtualBackground.options.simple6',
      thumbnail: '/images/virtualBackgrounds/simple6.thumbnail.webp',
    },
  },
  {
    index: 12,
    group: VirtualBackgroundGroups.Fun,
    name: 'Cartoon city street',
    filter: {
      type: 'backgroundReplacement',
      backgroundImgUrl: '/images/virtualBackgrounds/playful1.jpg',
    },
    metadata: {
      title: 'virtualBackground.options.playful1',
      thumbnail: '/images/virtualBackgrounds/playful1.thumbnail.webp',
    },
  },
  {
    index: 13,
    group: VirtualBackgroundGroups.Fun,
    name: 'Candy castle',
    filter: {
      type: 'backgroundReplacement',
      backgroundImgUrl: '/images/virtualBackgrounds/playful2.jpg',
    },
    metadata: {
      title: 'virtualBackground.options.playful2',
      thumbnail: '/images/virtualBackgrounds/playful2.thumbnail.webp',
    },
  },
  {
    index: 14,
    group: VirtualBackgroundGroups.Fun,
    name: 'Colorful mushroom world',
    filter: {
      type: 'backgroundReplacement',
      backgroundImgUrl: '/images/virtualBackgrounds/playful3.jpg',
    },
    metadata: {
      title: 'virtualBackground.options.playful3',
      thumbnail: '/images/virtualBackgrounds/playful3.thumbnail.webp',
    },
  },
  {
    index: 26,
    group: VirtualBackgroundGroups.Fun,
    name: 'Underwater world',
    filter: {
      type: 'backgroundReplacement',
      backgroundImgUrl: '/images/virtualBackgrounds/playful4.jpg',
    },
    metadata: {
      title: 'virtualBackground.options.playful4',
      thumbnail: '/images/virtualBackgrounds/playful4.thumbnail.webp',
    },
  },
  {
    index: 27,
    group: VirtualBackgroundGroups.Fun,
    name: 'Spaceship control room',
    filter: {
      type: 'backgroundReplacement',
      backgroundImgUrl: '/images/virtualBackgrounds/playful5.jpg',
    },
    metadata: {
      title: 'virtualBackground.options.playful5',
      thumbnail: '/images/virtualBackgrounds/playful5.thumbnail.webp',
    },
  },
  {
    index: 28,
    group: VirtualBackgroundGroups.Fun,
    name: 'Outer space with planets',
    filter: {
      type: 'backgroundReplacement',
      backgroundImgUrl: '/images/virtualBackgrounds/playful6.jpg',
    },
    metadata: {
      title: 'virtualBackground.options.playful6',
      thumbnail: '/images/virtualBackgrounds/playful6.thumbnail.webp',
    },
  },
];
